/**
 * Host-side lobby + room lock, on the room channel.
 *
 * Both surfaces that show waiting people — the "Lobby" pill over the video
 * grid and the Participants panel's lobby section — used to render hardcoded
 * arrays ("Alice Johnson", "sw / 1 unconfirmed user"), so admitting did
 * nothing but splice a local list. This hook is the single real source for
 * both.
 *
 * Nothing is fetched and nothing is polled. Signaling hands a moderator the
 * waiting room as it stands when they join — or when they are made one
 * mid-meeting — as `lobby:updated` and `room:lock_changed`, and sends
 * `lobby:updated` again whenever the list changes: a knock, a decision, an
 * unlock, a host arriving for those held for one, somebody leaving the waiting
 * room or going quiet (its own 15-second sweep, which this hook used to drive
 * by re-reading the list). Decisions go out on the same channel —
 * `lobby:admit`, `lobby:deny`, `lobby:lock` — instead of through the inMeet
 * backend, which asked signaling for the caller's role and then asked it again
 * to act.
 *
 * Subscribed whatever the role. The first `lobby:updated` arrives with the
 * join, before this client's roles do, and signaling sends the list to
 * moderators only, so an attendee's hook simply never hears it. `isHost` gates
 * the commands, which signaling refuses from anyone else anyway.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { LobbyRequest } from '@/features/meetings/services/meetings.api'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'

export type LobbyState = {
  /** People waiting for admission. Empty for non-hosts. */
  requests: LobbyRequest[]
  /** Server truth for the room lock, kept in sync by `room:lock_changed`. */
  locked: boolean
  /** True while a lock/unlock round-trip is in flight. */
  lockPending: boolean
  admit: (requestId: string) => void
  deny: (requestId: string) => void
  admitAll: () => void
  setLocked: (locked: boolean) => void
}

type Args = {
  isHost: boolean
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  /** The room channel's `request`: never rejects, resolves to the server's answer. */
  request: (event: string, payload: Record<string, unknown>) => Promise<{ status: string; response: Record<string, unknown> }>
}

export function useLobby({ isHost, subscribe, request }: Args): LobbyState {
  const [requests, setRequests] = useState<LobbyRequest[]>([])
  // A room starts unlocked; the push on joining and any broadcast correct this.
  const [locked, setLockedState] = useState(false)
  const [lockPending, setLockPending] = useState(false)
  // Rows with a decision in flight. A `lobby:updated` broadcast triggered by
  // someone else knocking can land before the server has processed our admit,
  // and its list still contains the row we just removed — which would put it
  // back and make the click look like it did nothing.
  const decidingRef = useRef<Set<string>>(new Set())

  const withoutDeciding = useCallback(
    (list: LobbyRequest[]) => list.filter((r) => !decidingRef.current.has(r.id)),
    [],
  )

  // Ids are sent back exactly as signaling sent them, `<room_id>~<request_id>`:
  // it checks the room part against the room the moderator is in. (The old
  // backend route needed the prefix to find the room; this used to add it from
  // the meeting CODE, which is not the room id.)

  // The list as it stands, for putting a row back after a decision that failed.
  const requestsRef = useRef<LobbyRequest[]>([])
  requestsRef.current = requests

  // `lobby:updated` carries the full pending list, so it replaces rather than
  // patches — no reconciliation, no drift.
  useEffect(() => {
    const offUpdated = subscribe('lobby:updated', (payload) => {
      const next = Array.isArray(payload.requests) ? (payload.requests as LobbyRequest[]) : []
      vclog('presence', 'lobby updated', { waiting: next.length })
      setRequests(withoutDeciding(next))
    })

    const offLock = subscribe('room:lock_changed', (payload) => {
      if (typeof payload.locked === 'boolean') {
        vclog('presence', 'room lock changed', { locked: payload.locked })
        setLockedState(payload.locked)
      }
    })

    return () => {
      offUpdated()
      offLock()
    }
  }, [subscribe, withoutDeciding])

  // Optimistic: drop the row immediately so the list does not sit there
  // looking unresponsive. The `lobby:updated` that follows is authoritative.
  const decide = useCallback(
    (requestId: string, action: 'admit' | 'deny') => {
      if (!isHost || decidingRef.current.has(requestId)) return
      const row = requestsRef.current.find((r) => r.id === requestId)
      decidingRef.current.add(requestId)
      setRequests((prev) => prev.filter((r) => r.id !== requestId))
      void request(`lobby:${action}`, { requestId }).then((reply) => {
        // Gone already — they left, or another moderator got there first —
        // is as good as done: there is no row to come back to.
        if (reply.status === 'ok' || reply.response.reason === 'not_found') {
          // Kept suppressed briefly: a list sent before the decision may still
          // be in flight behind it.
          setTimeout(() => decidingRef.current.delete(requestId), 3000)
          return
        }
        // Refused, or never answered: put it back so the host can try again.
        vclog('presence', 'lobby decision not carried out', { action, status: reply.status, reason: reply.response.reason })
        decidingRef.current.delete(requestId)
        if (row) setRequests((prev) => (prev.some((r) => r.id === row.id) ? prev : [...prev, row]))
      })
    },
    [isHost, request],
  )

  const admit = useCallback((id: string) => decide(id, 'admit'), [decide])
  const deny = useCallback((id: string) => decide(id, 'deny'), [decide])

  const admitAll = useCallback(() => {
    requestsRef.current.forEach((r) => decide(r.id, 'admit'))
  }, [decide])

  const setLocked = useCallback(
    (next: boolean) => {
      if (!isHost) return
      setLockPending(true)
      setLockedState(next) // optimistic; the reply or `room:lock_changed` settles it
      void request('lobby:lock', { locked: next })
        .then((reply) => {
          const locked = reply.response.locked
          setLockedState(reply.status === 'ok' && typeof locked === 'boolean' ? locked : !next)
        })
        .finally(() => setLockPending(false))
    },
    [isHost, request],
  )

  return { requests, locked, lockPending, admit, deny, admitAll, setLocked }
}
